import React, {useCallback} from "react";
import {QuestionWrapper} from "./common";
import {observer} from "mobx-react";
import styled, {keyframes} from "styled-components";
import {useStore, useWindowSize} from "modules/hooks";
import {IMAGES_URL, LOCALE} from "modules/constants";
import {get} from "lodash";
import Confetti from "react-confetti";
import bg from "assets/images/result-screen-background.png";
import bgMobile from "assets/images/CTA-background-mobile.png";

const fadeIn = keyframes`
	from {
		opacity: 0;
	}

	to {
		opacity: 1;
	}
`;

const Wrapper = styled(QuestionWrapper)`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 80px 26px 140px;
	box-sizing: border-box;
	background: url(${bg}) no-repeat center #010101;
	background-size: cover;

	@media (max-width: 840px) {
		background-image: url(${bgMobile});
	}
`;

const SubTitle = styled.h3`
	text-align: center;
	font-size: 30px;
	margin: 0 0 60px;
	line-height: 34px;
	animation: ${fadeIn} 2s ease-in-out 5s;
	animation-fill-mode: both;

	@media (max-width: 840px) {
		font-size: 24px;
		max-width: 230px;
		margin: 40px 0 30px;
	}
`;

const TeamName = styled.h1`
	text-align: center;
	font-size: 48px;
	line-height: 58px;
	font-weight: bold;
	animation: ${fadeIn} 2s ease-in-out 5s;
	animation-fill-mode: both;
	margin-bottom: 35px;

	@media (max-width: 840px) {
		display: none;
	}
`;

const TeamNameMobile = styled(TeamName)`
	display: none;
	margin-bottom: 60px;

	@media (max-width: 840px) {
		display: block;
	}
`;

const TeamLogo = styled.img`
	width: 350px;
	margin-bottom: 60px;
	animation: ${fadeIn} 2s ease-in-out 5s;
	animation-fill-mode: both;

	@media (max-width: 840px) {
		width: 230px;
		margin-bottom: 20px;
	}
`;

const Title = styled.h2`
	font-size: 30px;
	line-height: 34px;
	animation: ${fadeIn} 2s ease-in-out 2s;
	animation-fill-mode: both;
	margin: 160px 0 15px;
	text-align: center;

	@media (max-width: 840px) {
		margin-bottom: 70px;
		max-width: 230px;
	}
`;

const titles = {
	REDBLACKS: "screens.result.titleRB",
};

export const ResultScreen: React.FC = observer(() => {
	const {height, width} = useWindowSize();
	const {UISquadList, I18n, UIQuestionsProgress} = useStore();
	const topSquad = UISquadList.topSquad;
	const logo = `${IMAGES_URL}image/result/${topSquad?.shortName}.svg`;
	const name = get(topSquad?.fullName, LOCALE, "");
	const title = get(titles, topSquad?.shortName, "screens.result.title") as string;

	const animationEndHandler = useCallback(() => {
		setTimeout(() => UIQuestionsProgress.scrollToFinalPage(), 2000);
	}, [UIQuestionsProgress]);

	return (
		<Wrapper>
			<Title>{I18n.t(title)}</Title>
			<TeamName>{name}</TeamName>
			<TeamLogo src={logo} alt={name} onAnimationEnd={animationEndHandler} />
			<TeamNameMobile>{name}</TeamNameMobile>
			<SubTitle>{I18n.t("screens.result.subTitle")}</SubTitle>
			<Confetti
				width={width}
				height={height}
				colors={["#D9D9D9", "#00FFFF", "#1C1C1C", "#470000", "#4babab"]}
				numberOfPieces={400}
				opacity={0.9}
			/>
		</Wrapper>
	);
});
